/**
 * Pricing grid component - initialization, cart pre-population, and subtotal calculations
 */

import { formatNumber } from "../utils/formatters.js";
import { ensureProductsLoaded } from "./lazyProductLoader.js";

/**
 * Initialize pricing grid functionality
 */
export function initPricingGrid(): void {
  // Validate size alignment between header and product rows
  validateSizeAlignment();
}

/**
 * Compute a uniform height for every Update Cart button on the page, based
 * on how many warehouse rows of the first visible product are currently
 * visible (visibility is altered by the Timeframe filter).
 *
 * - visible row count > 1 (multi-warehouse actually showing) → 80 px, so
 *   the button visually ties to the multiple warehouse rows.
 * - visible row count === 1 (single warehouse after filtering) → 30 px,
 *   so the button stays compact.
 *
 * Publishes the chosen value as the CSS variable `--row-update-cart-height`
 * on :root, which `.row-update-cart` consumes. Safe to call whenever
 * row visibility changes (e.g. the Timeframe filter is re-applied).
 */
export function applyUniformUpdateCartButtonHeight(): void {
  // Do NOT use jQuery's `:visible` here. Rows hidden by the availability
  // filter get `visibility: collapse` + `height: 0 !important`, but they
  // still have non-zero offsetWidth (rows naturally span the full table),
  // so jQuery treats them as visible. Check offsetHeight directly instead.
  const allRows = Array.from(
    document.querySelectorAll<HTMLElement>(".warehouse_row"),
  );
  const isVisible = (el: HTMLElement): boolean => el.offsetHeight > 0;

  const firstVisible = allRows.find(isVisible);
  if (!firstVisible) return;

  const firstHandle = firstVisible.dataset.productHandle;
  if (!firstHandle) return;

  const visibleRowCount = allRows.filter(
    (r) => r.dataset.productHandle === firstHandle && isVisible(r),
  ).length;

  const heightPx = visibleRowCount > 1 ? 80 : 30;

  document.documentElement.style.setProperty(
    "--row-update-cart-height",
    `${heightPx}px`,
  );
}

/**
 * Pin every pricing-grid product image to one uniform height, derived from the
 * FIRST product's warehouse-row count.
 *
 * Images were previously sized to fill the cell (height: 100% / flex), so their
 * rendered size tracked the row height — which varies with warehouse count and
 * even per color (warehouse rows are inventory-gated), producing inconsistent,
 * distorted images. Here we read the first product's `data-warehouse-count`
 * (server-rendered on .product_image_cell, the full count regardless of
 * availability filtering) and set a single CSS var consumed by
 * `.productImageWithBadge img`. Every image on the page — including lazy-loaded
 * rows, which inherit the var — then renders at the same height.
 *
 * Runs once on init. By design we do NOT recompute when rows are later
 * hidden/shown by filters (matches applyUniformUpdateCartButtonHeight).
 */
export function applyUniformProductImageHeight(): void {
  const firstImageCell = document.querySelector<HTMLElement>(
    ".product_image_cell[data-warehouse-count]",
  );
  if (!firstImageCell) return;

  const warehouseCount = Number.parseInt(
    firstImageCell.dataset.warehouseCount ?? "",
    10,
  );
  if (!Number.isFinite(warehouseCount) || warehouseCount < 1) return;

  // base 40px + 80px per warehouse row → 1:120px, 2:200px, 3:280px, ...
  const heightPx = 40 + warehouseCount * 80;

  document.documentElement.style.setProperty(
    "--grid-img-height",
    `${heightPx}px`,
  );
}

/**
 * Validate that all product row sizes exist in the header.
 * Logs warnings to console if mismatches are detected.
 * This helps catch data issues where a color has sizes not present in the first product.
 */
function validateSizeAlignment(): void {
  // Get header sizes from the header row
  const headerSizes = new Set<string>();
  $(".header_size_cell .header_size").each(function () {
    const size = $(this).text().trim();
    if (size) {
      headerSizes.add(size);
    }
  });

  if (headerSizes.size === 0) {
    // No header found, likely not on a product page with pricing grid
    return;
  }

  // Track products with issues for summary
  const productsWithIssues: Array<{
    color: string;
    handle: string;
    missingSizes: string[];
  }> = [];

  // Get unique product handles to check each product once
  const checkedHandles = new Set<string>();

  $(".warehouse_row").each(function () {
    const $row = $(this);
    const handle = $row.attr("data-product-handle");
    const color = $row.attr("data-inventory-color") || "Unknown";

    // Only check each product once (not every warehouse row)
    if (!handle || checkedHandles.has(handle)) {
      return;
    }
    checkedHandles.add(handle);

    // Get sizes from this product's variant cells
    const rowSizes: string[] = [];
    $row
      .find(".variant_cell")
      .not(".variant_cell_empty")
      .each(function () {
        const size = $(this).attr("data-variant-title");
        if (size) {
          rowSizes.push(size);
        }
      });

    // Find sizes in row that are NOT in header
    const missingSizes = rowSizes.filter((size) => !headerSizes.has(size));

    if (missingSizes.length > 0) {
      productsWithIssues.push({
        color,
        handle,
        missingSizes,
      });
    }
  });

  // Log warnings for any issues found
  if (productsWithIssues.length > 0) {
    console.warn(
      "%c⚠️ PRICING GRID SIZE MISMATCH DETECTED",
      "color: #ff6600; font-weight: bold; font-size: 14px;",
    );
    console.warn(
      "The following products have sizes that do NOT exist in the header row.",
    );
    console.warn(
      "This will cause incorrect cart additions! Please fix the product data.",
    );
    console.warn("Header sizes:", Array.from(headerSizes).join(", "));
    console.warn("---");

    productsWithIssues.forEach(({ color, handle, missingSizes }) => {
      console.warn(
        `%c${color}%c (${handle}): Missing from header: %c${missingSizes.join(", ")}`,
        "color: #cc0000; font-weight: bold;",
        "color: inherit;",
        "color: #cc0000; font-weight: bold;",
      );
    });

    console.warn("---");
    console.warn(
      "To fix: Ensure the first product (Black) has ALL sizes that any color in the family might have.",
    );
  }
}

/**
 * Pre-populate grid inputs from current cart.
 *
 * When lazy loading is active, Liquid pre-computes which product handles have
 * cart quantities and stores them in window.__productFamilyPreload.  We load
 * those rows first so variant_id matching finds them in the DOM.
 */
export async function prePopulateFromCart(): Promise<void> {
  // Eager-load any products that Liquid identified as having cart quantities
  const preloadHandles = window.__productFamilyPreload;
  if (Array.isArray(preloadHandles) && preloadHandles.length > 0) {
    await ensureProductsLoaded(preloadHandles);
  }

  try {
    const response = await fetch("/cart.js");
    const cart = (await response.json()) as {
      items: Array<{
        variant_id: number;
        quantity: number;
        properties?: { _warehouse_code?: string };
      }>;
    };

    if (cart.items && cart.items.length > 0) {
      const inputsByKey = new Map<string, HTMLInputElement>();
      $(".variant_qty_input_directship").each(function () {
        const input = this as HTMLInputElement;
        const { variantId, warehouseCode } = input.dataset;
        if (variantId) {
          inputsByKey.set(`${variantId}|${warehouseCode || "WPBR"}`, input);
        }
      });

      cart.items.forEach(function (item) {
        const key = `${item.variant_id}|${item.properties?._warehouse_code || "WPBR"}`;
        const input = inputsByKey.get(key);
        if (input) {
          input.value = String(item.quantity);
          input.classList.add("has-qty");
        }
      });

      updateAllSubtotals();
    }
  } catch (error) {
    console.error("Error fetching cart for pre-population:", error);
  }
}

/**
 * Update all subtotals across the grid
 */
function updateAllSubtotals(): void {
  const productHandles = new Set<string>();
  $(".warehouse_row").each(function () {
    const handle = $(this).attr("data-product-handle");
    if (handle) {
      productHandles.add(handle);
    }
  });

  productHandles.forEach(function (handle) {
    const $allRows = $(`.warehouse_row[data-product-handle="${handle}"]`);
    if ($allRows.length > 0) {
      updateTableRowSubtotals($allRows);
    }
  });
}

/**
 * Update subtotals for table-based structure
 * @param $allProductRows - All warehouse rows for a specific product
 */
export function updateTableRowSubtotals($allProductRows: JQuery): void {
  let totalQty = 0;
  let totalPrice = 0;

  $allProductRows.find(".variant_cell").each(function () {
    const $cell = $(this);
    const price = parseFloat($cell.attr("data-variant-price") ?? "0") || 0;
    const qty =
      parseInt($cell.find(".variant_qty_input_directship").val() as string) ||
      0;

    totalQty += qty;
    totalPrice += qty * price;
  });

  $allProductRows
    .first()
    .find(".location_int_subtotal")
    .text(formatNumber(totalQty));
  $allProductRows
    .first()
    .find(".location_int_pricetotal")
    .text(
      "$" +
        new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(totalPrice),
    );
}
